import React, { Component } from 'react';
import { connect } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import { NextRouter } from 'next/router';


import { Button } from 'semantic-ui-react';
import styles from './Dashboard.module.scss';
import Tickets from './Tickets/Tickets';
import Feed from './Feed/Feed';
import OrgList from './OrgList/OrgList';
import Explore from './Explore/Explore';
import { connectVerificationFlow } from '../../../util/modalFlows';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import DashboardFeatureReminders from '../../../feature-reminder/screens/DashboardFeatureReminders.screen';
import AssociationsList from './AssociationsList/AssociationsList';

interface DashboardProps extends WithTranslation {
  user: any;
  router: NextRouter;
  tickets: any[];
  nearbyEvents: any[];
  newsFeed: any[];
  device: 'mobile' | 'tablet' | 'desktop';
}

interface DashboardState {
  view: 'feed' | 'explore' | 'tickets';
}

class Dashboard extends Component<DashboardProps, DashboardState> {
  constructor(props: DashboardProps) {
    super(props);
    this.state = {
      view: 'feed'
    };
  }

  componentDidMount = () => {
    if (this.props.user && this.props.user.signupRequirements?.length === 0) {
      // Without a setTimeout, the useEffect call to close modals on route transitions
      // causes the modal to close instantly after opening.
      setTimeout(() => {
        connectVerificationFlow(this.props.user.connectVerification);
      }, 1000);
    }
  };

  navigateToEvent = (_id: string) => {
    this.props.router.push(`/events/${_id}`);
  };

  activePage = () => {
    const userOrgs = this.props.user?.organizations?.items;
    switch (this.state.view) {
      case 'tickets':
        return (
          <div className="page">
            <Tickets tickets={this.props.tickets} device={this.props.device} />
          </div>
        );
      case 'explore':
        return (
          <div className="page">
            <Explore
              nearbyEvents={this.props.nearbyEvents}
              device={this.props.device}
              navigateToEvent={this.navigateToEvent}
            />
          </div>
        );
      case 'feed':
        return (
          <div className="page">
            {userOrgs?.length > 0 ? (
              <OrgList
                device={this.props.device}
                orgs={userOrgs || []}
              />
            ) : (
              <Feed
                device={this.props.device}
                newsFeed={this.props.newsFeed}
              />
            )}
          </div>
        );
      default:
        return (
          <div className="page">
            <Explore
              nearbyEvents={this.props.nearbyEvents}
              device={this.props.device}
              navigateToEvent={this.navigateToEvent}
            />
          </div>
        );
    }
  };

  renderBottomBar = () => {
    const { t } = this.props;
    return (
      <Button.Group className="navigation">
        <Button
          className={this.state.view === 'feed' && 'active'}
          onClick={() => this.setState({ view: 'feed' })}
        >
          {t('navigation.feed')}
        </Button>
        <Button
          className={this.state.view === 'explore' && 'active'}
          onClick={() => this.setState({ view: 'explore' })}
        >
          {t('navigation.explore')}
        </Button>
        <Button
          className={this.state.view === 'tickets' && 'active'}
          onClick={() => this.props.router.push(`/tickets`)}
        >
          {t('navigation.tickets')}
        </Button>
      </Button.Group>
    );
  };

  render() {
    const userOrgs = this.props.user?.organizations?.items;
    return (
      <div className={classnames([styles.Home], this.props.device)}>
        <DashboardFeatureReminders />

        {this.props.user && (
          <div className="wrapper">
            {this.props.device !== 'mobile' && (
              <div className="bn-row">
                <div className="col col1">
                  <Tickets
                    tickets={this.props.tickets}
                    device={this.props.device}
                  />
                  <Explore
                    nearbyEvents={this.props.nearbyEvents}
                    device={this.props.device}
                    navigateToEvent={this.navigateToEvent}
                  />
                </div>
                <div className="col col2">
                  <AssociationsList device={this.props.device} />
                  {userOrgs?.length > 0 ? (
                    <OrgList
                      device={this.props.device}
                      orgs={userOrgs || []}
                    />
                  ) : (
                    <Feed
                      device={this.props.device}
                      newsFeed={this.props.newsFeed}
                    />
                  )}
                </div>
              </div>
            )}

            {this.props.device === 'mobile' && (
              <div className="bn-row swipeable">
                <TransitionGroup className="page-switch">
                  <CSSTransition
                    key={this.state.view}
                    timeout={{ enter: 300, exit: 300 }}
                    classNames="fade"
                  >
                    {this.activePage()}
                  </CSSTransition>
                </TransitionGroup>
              </div>
            )}

            {this.props.device === 'mobile' && this.renderBottomBar()}
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state: any) {
  return {
    user: state.user.user,
    tickets: state.events.tickets,
    nearbyEvents: state.events.nearbyEvents,
    newsFeed: state.user.newsFeed,
    device: state.app.device
  };
}

const DashboardWithTranslation = withTranslation('dashboard')(Dashboard as any) as any;
const DashboardWithRedux = connect(mapStateToProps)(DashboardWithTranslation as any) as any;
export default withLDConsumer()(DashboardWithRedux as any) as any;
